import React, { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, Music } from "lucide-react";
import { cn } from "@/shared/utils";

interface SpotifyOutletProps {
  className?: string;
}

export const SpotifyOutlet: React.FC<SpotifyOutletProps> = ({ className }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Mock data - später durch echte Spotify API ersetzen
  const currentTrack = {
    title: "Bohemian Rhapsody",
    artist: "Queen",
    album: "A Night at the Opera",
    cover: "https://via.placeholder.com/40x40",
    progress: 65,
    duration: 354,
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className={cn("relative", className)}>
      {/* Spotify Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-800/80 transition-all duration-200"
        title="Spotify"
      >
        <Music className="w-5 h-5 text-green-500" />
        <div className="hidden md:block text-left">
          <p className="text-xs font-medium text-white truncate max-w-32">{currentTrack.title}</p>
          <p className="text-xs text-neutral-400 truncate max-w-32">{currentTrack.artist}</p>
        </div>
      </button>

      {/* Spotify Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-16 right-4 w-80 bg-neutral-800 rounded-xl border border-neutral-700/50 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-700/50">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-green-500" />
                <h3 className="font-semibold text-white">Spotify</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-neutral-400 hover:text-white transition-colors"
              >
                ×
              </button>
            </div>

            {/* Current Track */}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={currentTrack.cover}
                  alt={currentTrack.album}
                  className="w-12 h-12 rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{currentTrack.title}</p>
                  <p className="text-sm text-neutral-400 truncate">{currentTrack.artist}</p>
                  <p className="text-xs text-neutral-500 truncate">{currentTrack.album}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="w-full bg-neutral-700 rounded-full h-1">
                  <div
                    className="bg-green-500 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${currentTrack.progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-neutral-400 mt-1">
                  <span>{Math.floor((currentTrack.duration * currentTrack.progress) / 100)}s</span>
                  <span>{currentTrack.duration}s</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <button className="p-2 text-neutral-400 hover:text-white transition-colors">
                  <SkipBack className="w-5 h-5" />
                </button>
                <button
                  onClick={togglePlay}
                  className="p-3 bg-green-500 hover:bg-green-600 rounded-full transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white" />
                  )}
                </button>
                <button className="p-2 text-neutral-400 hover:text-white transition-colors">
                  <SkipForward className="w-5 h-5" />
                </button>
              </div>

              {/* Volume */}
              <div className="flex items-center gap-2 mt-4">
                <Volume2 className="w-4 h-4 text-neutral-400" />
                <div className="flex-1 bg-neutral-700 rounded-full h-1">
                  <div className="bg-green-500 h-1 rounded-full w-3/4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
